import { useEffect, useState, type RefObject } from 'react'
import { api } from '../../shared/api/http'

// Temporary field diagnostic: numeric media state only, never URLs or credentials.
export function usePlaybackDiagnostic(videoRef: RefObject<HTMLVideoElement | null>) {
  const [attempt, setAttempt] = useState<{ id: string; segment: string; offset: number; start: number; utc: string } | null>(null)
  useEffect(() => {
    const video = videoRef.current
    if (!attempt || !video) return
    const events: { name: string; elapsedMs: number; readyState: number; networkState: number; position: number; bufferedEnd: number; errorCode: number }[] = []
    let stopped = false, reports = 0, frame = 0, played = false
    const record = (name: string) => {
      if (events.length >= 40) return
      events.push({ name, elapsedMs: Math.round(performance.now() - attempt.start), readyState: video.readyState,
        networkState: video.networkState, position: Number.isFinite(video.currentTime) ? video.currentTime : 0,
        bufferedEnd: video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0, errorCode: video.error?.code ?? 0 })
    }
    const send = () => {
      if (reports++ >= 5) return
      void api(`/recordings/segments/${attempt.segment}/diagnostic`, { method: 'POST', keepalive: true,
        body: JSON.stringify({ attemptId: attempt.id, startedUtc: attempt.utc, offset: attempt.offset, events }) }).catch(() => undefined)
    }
    const handler = (event: Event) => {
      record(event.type)
      if (event.type === 'playing') played = true
      if (event.type === 'playing' || event.type === 'error') send()
    }
    const names = ['loadstart', 'loadedmetadata', 'loadeddata', 'canplay', 'playing', 'waiting', 'stalled', 'seeking', 'seeked', 'error']
    names.forEach(name => video.addEventListener(name, handler))
    record('selected')
    if ('requestVideoFrameCallback' in video) {
      const onFrame = () => {
        if (stopped) return
        if (!video.seeking && Math.abs(video.currentTime - attempt.offset) < 3) { record('frame'); send() }
        else frame = video.requestVideoFrameCallback(onFrame)
      }
      frame = video.requestVideoFrameCallback(onFrame)
    }
    const timer = window.setTimeout(() => { if (!played) { record('timeout'); send() } }, 30000)
    return () => {
      stopped = true
      window.clearTimeout(timer)
      if (frame) video.cancelVideoFrameCallback(frame)
      names.forEach(name => video.removeEventListener(name, handler))
      record('closed'); send()
    }
  }, [attempt, videoRef])
  return (segment: string, offset: number) => {
    const id = crypto.randomUUID()
    setAttempt({ id, segment, offset, start: performance.now(), utc: new Date().toISOString() })
    return id
  }
}
